import { API } from "@/lib/api";

const STORY_W = 1080;
const STORY_H = 1920;

const COLOR_BG = "#FDFCF6";
const COLOR_TEXT = "#1A1814";
const COLOR_ORANGE = "#F5A623";
const COLOR_MUTED = "#5C5850";
const COLOR_TRACK = "#EBE7DD";

const AMTM_LOGO_URL = "https://customer-assets-m6fa6gv7.emergentagent.net/job_amtm-animal-care/artifacts/6ng7d8bg_amtm_logo_trim.png";

function proxied(url) {
  return `${API}/image-proxy?url=${encodeURIComponent(url)}`;
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

async function ensureFontsLoaded() {
  if (!document.fonts) return;
  try {
    await Promise.all([
      document.fonts.load('700 96px "Fraunces"'),
      document.fonts.load('600 44px "Montserrat"'),
      document.fonts.load('500 38px "Montserrat"'),
      document.fonts.load('400 30px "Montserrat"'),
    ]);
    await document.fonts.ready;
  } catch (err) {
    // best-effort; canvas will fall back to default serif/sans-serif
    console.error("Font preload failed, using fallback fonts:", err);
  }
}

function drawCoverFit(ctx, img, x, y, w, h) {
  const imgRatio = img.width / img.height;
  const boxRatio = w / h;
  let sx, sy, sw, sh;
  if (imgRatio > boxRatio) {
    sh = img.height;
    sw = sh * boxRatio;
    sx = (img.width - sw) / 2;
    sy = 0;
  } else {
    sw = img.width;
    sh = sw / boxRatio;
    sx = 0;
    sy = (img.height - sh) / 2;
  }
  ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
}

function roundedRectPath(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function wrapText(ctx, text, maxWidth) {
  const words = String(text).split(/\s+/);
  const lines = [];
  let current = "";
  for (const word of words) {
    const test = current ? `${current} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && current) {
      lines.push(current);
      current = word;
    } else {
      current = test;
    }
  }
  if (current) lines.push(current);
  return lines;
}

/**
 * Generates a 1080x1920 Instagram-story-format PNG image for a public case:
 * hero photo, animal name, "Help [Name] recover" line, donation progress
 * (if a goal is set), the AMTM logo, and the case URL. Returns a Blob.
 */
export async function generateStoryImage(caseItem, coverPhotoUrl, pageUrl) {
  await ensureFontsLoaded();

  const canvas = document.createElement("canvas");
  canvas.width = STORY_W;
  canvas.height = STORY_H;
  const ctx = canvas.getContext("2d");

  ctx.fillStyle = COLOR_BG;
  ctx.fillRect(0, 0, STORY_W, STORY_H);

  const photoH = Math.round(STORY_H * 0.6);
  if (coverPhotoUrl) {
    try {
      const img = await loadImage(proxied(coverPhotoUrl));
      drawCoverFit(ctx, img, 0, 0, STORY_W, photoH);
    } catch {
      ctx.fillStyle = "#F7E98E";
      ctx.fillRect(0, 0, STORY_W, photoH);
    }
  } else {
    ctx.fillStyle = "#F7E98E";
    ctx.fillRect(0, 0, STORY_W, photoH);
  }

  // Soft gradient fade where the photo meets the text panel, for a clean edge
  const fade = ctx.createLinearGradient(0, photoH - 140, 0, photoH);
  fade.addColorStop(0, "rgba(253,252,246,0)");
  fade.addColorStop(1, COLOR_BG);
  ctx.fillStyle = fade;
  ctx.fillRect(0, photoH - 140, STORY_W, 140);

  const padX = 72;
  let y = photoH + 100;

  ctx.textAlign = "left";
  ctx.fillStyle = COLOR_TEXT;
  ctx.font = '700 92px "Fraunces", serif';
  ctx.fillText(caseItem.animal_name, padX, y);
  y += 88;

  ctx.font = '600 46px "Montserrat", sans-serif';
  ctx.fillStyle = COLOR_ORANGE;
  const headline = caseItem.status === "Sanctuary Resident"
    ? `Sponsor ${caseItem.animal_name} at our sanctuary \u2764`
    : `Help ${caseItem.animal_name} recover \u2764\ufe0f`;
  ctx.fillText(headline, padX, y);
  y += 90;

  const needed = caseItem.donation_needed || 0;
  const received = caseItem.donation_received_total ?? caseItem.donation_received ?? 0;
  if (needed > 0) {
    ctx.font = '500 38px "Montserrat", sans-serif';
    ctx.fillStyle = COLOR_TEXT;
    ctx.fillText(
      `\u20B9${received.toLocaleString("en-IN")} raised of \u20B9${needed.toLocaleString("en-IN")}`,
      padX,
      y
    );
    y += 56;

    const barW = STORY_W - padX * 2;
    const barH = 28;
    const pct = Math.max(0, Math.min(received / needed, 1));
    ctx.fillStyle = COLOR_TRACK;
    roundedRectPath(ctx, padX, y, barW, barH, barH / 2);
    ctx.fill();
    if (pct > 0) {
      ctx.fillStyle = COLOR_ORANGE;
      roundedRectPath(ctx, padX, y, Math.max(barW * pct, barH), barH, barH / 2);
      ctx.fill();
    }
    y += 90;
  } else {
    y += 20;
  }

  // AMTM logo, bottom-left
  const logoH = 76;
  try {
    const logo = await loadImage(proxied(AMTM_LOGO_URL));
    const logoW = logo.width * (logoH / logo.height);
    ctx.drawImage(logo, padX, STORY_H - 170, logoW, logoH);
  } catch {
    ctx.font = '700 40px "Fraunces", serif';
    ctx.fillStyle = COLOR_TEXT;
    ctx.fillText("AMTM", padX, STORY_H - 170 + logoH);
  }

  // Case URL, small text near the very bottom
  ctx.font = '400 30px "Montserrat", sans-serif';
  ctx.fillStyle = COLOR_MUTED;
  const urlText = String(pageUrl).replace(/^https?:\/\//, "");
  const urlLines = wrapText(ctx, urlText, STORY_W - padX * 2);
  let uy = STORY_H - 70;
  for (let i = urlLines.length - 1; i >= 0; i--) {
    ctx.fillText(urlLines[i], padX, uy);
    uy -= 38;
  }

  return new Promise((resolve) => canvas.toBlob((blob) => resolve(blob), "image/png"));
}

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Copies the case URL to the clipboard, then tries the native Web Share API
 * with the generated image file (so Instagram appears as a share target on
 * mobile). Falls back to downloading the image if sharing files isn't
 * supported (desktop) or unavailable. Always shows the same "link copied"
 * confirmation, except when the user explicitly cancels the share sheet.
 */
export async function shareOrDownloadStoryImage(blob, filename, pageUrl, toast) {
  try {
    await navigator.clipboard.writeText(pageUrl);
  } catch (err) {
    // clipboard access can fail silently (e.g. no permission) - not fatal
    console.error("Clipboard write failed:", err);
  }

  const file = new File([blob], filename, { type: "image/png" });
  const canShareFiles =
    typeof navigator.canShare === "function" && navigator.canShare({ files: [file] }) && typeof navigator.share === "function";

  if (canShareFiles) {
    try {
      await navigator.share({ files: [file] });
      toast({ title: "Link copied!", description: "Add it as a Link sticker on your story." });
      return;
    } catch (err) {
      if (err && err.name === "AbortError") return; // user cancelled the share sheet
      // fall through to download on any other failure
    }
  }

  downloadBlob(blob, filename);
  toast({ title: "Link copied!", description: "Add it as a Link sticker on your story." });
}


/**
 * Generates a 1080x1920 Instagram-story-format PNG for the Diwali campaign:
 * festive gold band, live raised total, and the AMTM logo/URL, reusing the
 * same canvas helpers as generateStoryImage above. Returns a Blob.
 */
export async function generateDiwaliStoryImage(raisedAmount, pageUrl) {
  await ensureFontsLoaded();

  const canvas = document.createElement("canvas");
  canvas.width = STORY_W;
  canvas.height = STORY_H;
  const ctx = canvas.getContext("2d");

  ctx.fillStyle = COLOR_BG;
  ctx.fillRect(0, 0, STORY_W, STORY_H);

  const bandH = Math.round(STORY_H * 0.4);
  const glow = ctx.createLinearGradient(0, 0, 0, bandH);
  glow.addColorStop(0, "#FFD85F");
  glow.addColorStop(1, "#F5A623");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, STORY_W, bandH);

  const fade = ctx.createLinearGradient(0, bandH - 140, 0, bandH);
  fade.addColorStop(0, "rgba(253,252,246,0)");
  fade.addColorStop(1, COLOR_BG);
  ctx.fillStyle = fade;
  ctx.fillRect(0, bandH - 140, STORY_W, 140);

  const padX = 72;
  ctx.textAlign = "left";

  ctx.font = '600 40px "Montserrat", sans-serif';
  ctx.fillStyle = "#1F0E06";
  ctx.fillText("THIS DIWALI", padX, 140);

  ctx.font = '700 80px "Fraunces", serif';
  const headlineLines = wrapText(ctx, "Diwali for the whole AMTM family", STORY_W - padX * 2);
  let y = 250;
  for (const line of headlineLines) {
    ctx.fillText(line, padX, y);
    y += 88;
  }

  y = bandH + 110;
  ctx.font = '500 38px "Montserrat", sans-serif';
  ctx.fillStyle = COLOR_MUTED;
  ctx.fillText("Together we have raised", padX, y);

  y += 110;
  ctx.font = '700 96px "Fraunces", serif';
  ctx.fillStyle = "#0C7268";
  ctx.fillText(`\u20B9${Math.round(raisedAmount || 0).toLocaleString("en-IN")}`, padX, y);

  y += 70;
  ctx.font = '500 38px "Montserrat", sans-serif';
  ctx.fillStyle = COLOR_TEXT;
  const bodyLines = wrapText(ctx, "for festive meals and a thank you to the caregivers who look after 500+ rescued animals every day.", STORY_W - padX * 2);
  for (const line of bodyLines) {
    y += 56;
    ctx.fillText(line, padX, y);
  }

  const logoH = 76;
  try {
    const logo = await loadImage(proxied(AMTM_LOGO_URL));
    const logoW = logo.width * (logoH / logo.height);
    ctx.drawImage(logo, padX, STORY_H - 170, logoW, logoH);
  } catch {
    ctx.font = '700 40px "Fraunces", serif';
    ctx.fillStyle = COLOR_TEXT;
    ctx.fillText("AMTM", padX, STORY_H - 170 + logoH);
  }

  ctx.font = '400 30px "Montserrat", sans-serif';
  ctx.fillStyle = COLOR_MUTED;
  const urlText = String(pageUrl).replace(/^https?:\/\//, "");
  const urlLines = wrapText(ctx, urlText, STORY_W - padX * 2);
  let uy = STORY_H - 70;
  for (let i = urlLines.length - 1; i >= 0; i--) {
    ctx.fillText(urlLines[i], padX, uy);
    uy -= 38;
  }

  return new Promise((resolve) => canvas.toBlob((blob) => resolve(blob), "image/png"));
}
